<script setup>
import { useShareLink } from "@/composables/useShareLink";
import { computed, ref } from "vue";
import { useAuthUser } from "@/composables/useAuthUser.js";
import { useModalogStore } from "@/stores/modalog.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import { resolveProfileTheme } from "./profileThemes.js";
import { DEFAULT_WELCOME_EMOJI } from "./welcomeEmoji.js";
import { useInertiaAction } from "@/composables/useInertiaAction.js";

const props = defineProps({
    owner: {
        type: Object,
        required: true,
    },
    isOwner: {
        type: Boolean,
        default: false,
    },
    // Theme slug; falls back to the default theme (see profileThemes.js).
    theme: {
        type: String,
        default: undefined,
    },
});

const authUser = useAuthUser();
const { showModalog } = useModalogStore();
const theme = computed(() => resolveProfileTheme(props.theme));

function openManageProfileModalog(focus = null) {
    showModalog({
        type: "ModalogManageProfileTheme",
        props: focus ? { focus } : {},
    });
}

const ownerDisplayName = computed(() =>
    props.owner.is_organization
        ? props.owner.presentation_name
        : props.owner.full_name,
);

// Names that already end in a possessive ("Grandma's Favorite's") or an s
// don't get another "'s" bolted on.
const heroTitle = computed(() =>
    /['’]s$/i.test(ownerDisplayName.value ?? "")
        ? `${ownerDisplayName.value} Lists`
        : `${ownerDisplayName.value}’s Lists`,
);

const statusMessage = computed(() => props.owner.short_description);

// Owner-picked emoji beside the message (BH-41), with the classic default.
const welcomeEmoji = computed(
    () => props.owner.welcome_emoji || DEFAULT_WELCOME_EMOJI,
);

// Custom header photo (GH-892): rendered over the theme gradient with a
// dark wash, while the wedge/shade decorations stay on top. Sized to the
// hero (1472x264 max) via Cloudinary, mirroring useMastheadImageUrls.
// Owner-chosen focal point (GH-1211); null keeps the centred crop.
const headerFocus = computed(() =>
    Number.isFinite(props.owner.header_focus_x) &&
    Number.isFinite(props.owner.header_focus_y)
        ? { x: props.owner.header_focus_x, y: props.owner.header_focus_y }
        : null,
);

const headerPhoto = computed(() => {
    if (!props.owner.header_photo) {
        return null;
    }

    // With a focal point the crop centres on it (relative g_xy_center, as
    // the list covers do) and the <img> keeps the same spot in frame.
    const gravity = headerFocus.value
        ? `g_xy_center,x_${(headerFocus.value.x / 100).toFixed(2)},y_${(headerFocus.value.y / 100).toFixed(2)}`
        : "g_center";
    const transform = (dpr) =>
        `https://res.cloudinary.com/gift-hero/image/upload/dpr_${dpr},c_lfill,h_264,w_1472,${gravity},q_auto:good/${props.owner.header_photo}.jpg`;

    return {
        x1: transform("1.0"),
        x2: transform("2.0"),
        position: headerFocus.value
            ? `${headerFocus.value.x}% ${headerFocus.value.y}%`
            : null,
    };
});

// Fallback to an initial-letter disc when the avatar is missing or 404s.
const avatarFailed = ref(false);

// --- Copy profile URL (owner) ---
const { shareLink } = useShareLink();
const copied = ref(false);

async function copyProfileUrl() {
    // Tagged before the write so the clipboard call stays inside the user
    // gesture; shareLink() reports the token without awaiting (GH-1192).
    const url = shareLink(props.owner.profile_url, {
        type: "user",
        uuid: props.owner.uuid,
    });

    await navigator.clipboard?.writeText(url);
    copied.value = true;
    setTimeout(() => (copied.value = false), 2000);
}

// --- Follow / unfollow (visitor) ---
const { busy: followBusy, run: runFollow } = useInertiaAction();

const followLabel = computed(() => {
    const shortName = props.owner.is_organization
        ? props.owner.presentation_name
        : props.owner.first_name;

    // Progressive verb while the request runs (GH-1001); owner.following
    // still holds the pre-toggle state until the reload lands.
    if (followBusy.value) {
        return props.owner.following ? "Unfollowing" : `Following ${shortName}`;
    }

    if (props.owner.following) {
        return "Unfollow";
    }

    return `Follow ${shortName}`;
});

// The endpoints return a bare boolean (not an Inertia response), so this
// goes through axios and reloads props afterwards.
async function toggleFollow() {
    if (followBusy.value) {
        return;
    }

    // Busy spans the request and the reload (GH-1159) so the button lands
    // directly in its new state.
    await runFollow(() =>
        props.owner.following
            ? axios.delete(route("user.unfollow", { user: props.owner.uuid }))
            : axios.post(route("user.follow", { user: props.owner.uuid })),
    );
}
</script>

<template>
    <section class="relative">
        <!-- Mobile top strip per Figma 224-482 (GH-1078) -->
        <div class="h-1.5 lg:hidden" :style="{ background: theme.wedge }"></div>
        <!-- Backdrop carries the gradient and clips the decorations. On
             mobile it stops at the avatar's midline (the avatar straddles
             the band edge); on lg it covers the whole hero. -->
        <div
            class="pointer-events-none absolute inset-x-0 top-1.5 h-[164px] overflow-hidden lg:inset-0 lg:top-0 lg:h-auto"
            :style="{ backgroundImage: theme.gradient }"
        >
            <!-- Custom header photo sits over the gradient (which doubles as
                 the loading fallback) under a dark legibility wash; the theme
                 decorations below still render on top of it. -->
            <template v-if="headerPhoto">
                <img
                    :src="headerPhoto.x1"
                    :srcset="`${headerPhoto.x1} 1x, ${headerPhoto.x2} 2x`"
                    alt=""
                    class="absolute inset-0 size-full object-cover"
                    :class="headerPhoto.position ? '' : 'object-bottom'"
                    :style="
                        headerPhoto.position
                            ? { objectPosition: headerPhoto.position }
                            : {}
                    "
                    data-test="profile-header-photo"
                />
                <div class="absolute inset-0 bg-[#060e13]/50"></div>
            </template>
            <!-- Corner wedge + right-hand shade band. Both edges run at the
                 same diagonal (dx/dy ≈ 0.91) so they stay parallel to the
                 gradient's 228deg isolines, per the Figma geometry. -->
            <div
                class="absolute inset-y-0 left-0 w-[172px] max-lg:hidden lg:w-[240px]"
                :style="{
                    background: theme.wedge,
                    clipPath: 'polygon(0 0, 100% 100%, 0 100%)',
                }"
            ></div>
            <div
                class="absolute inset-y-0 right-0 w-[460px] max-lg:hidden lg:w-[640px]"
                :style="{
                    background: theme.shade,
                    clipPath:
                        'polygon(26.25% 0, 100% 0, 100% 100%, 63.75% 100%)',
                }"
            ></div>
        </div>

        <div
            class="relative flex flex-col items-center gap-4 px-6 pt-7 max-lg:pb-4 lg:min-h-[264px] lg:flex-row lg:items-center lg:gap-9 lg:px-10 lg:py-8"
        >
            <!-- Mobile: name above the avatar -->
            <h1
                class="text-center font-brand-display text-[30px] font-black italic uppercase leading-[1.1] text-white lg:hidden"
            >
                {{ heroTitle }}
            </h1>

            <!-- Avatar; on mobile the owner actions flank it -->
            <div
                class="flex w-full items-center gap-4 max-lg:items-start lg:w-auto"
                :class="isOwner ? 'justify-between' : 'justify-center'"
            >
                <button
                    v-if="isOwner"
                    type="button"
                    class="grid size-10 place-items-center rounded-brand bg-black/25 text-white transition hover:bg-black/40 max-lg:mt-6 lg:hidden"
                    aria-label="Manage profile and theme"
                    @click="openManageProfileModalog"
                >
                    <svg
                        viewBox="0 0 17 17"
                        class="size-[17px]"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            d="M6.74742 17L6.22165 14.45C5.87113 14.3083 5.54282 14.1599 5.2367 14.0046C4.93058 13.8493 4.61629 13.6578 4.29381 13.43L1.75258 14.195L0 11.305L2.01546 9.605C1.95704 9.23667 1.92783 8.86833 1.92783 8.5C1.92783 8.13167 1.95704 7.76333 2.01546 7.395L0 5.695L1.75258 2.805L4.29381 3.57C4.61512 3.34333 4.92942 3.15237 5.2367 2.9971C5.54399 2.84183 5.8723 2.6928 6.22165 2.55L6.74742 0H10.2526L10.7783 2.55C11.1289 2.69167 11.4578 2.8407 11.765 2.9971C12.0723 3.1535 12.386 3.34447 12.7062 3.57L15.2474 2.805L17 5.695L14.9845 7.395C15.043 7.76333 15.0722 8.13167 15.0722 8.5C15.0722 8.86833 15.043 9.23667 14.9845 9.605L17 11.305L15.2474 14.195L12.7062 13.43C12.3849 13.6567 12.0712 13.8482 11.765 14.0046C11.4589 14.161 11.13 14.3095 10.7783 14.45L10.2526 17H6.74742ZM10.9764 10.9004C11.6622 10.2363 12.0052 9.43613 12.0052 8.5C12.0052 7.56387 11.6622 6.76373 10.9764 6.0996C10.2905 5.43547 9.46509 5.10227 8.5 5.1C7.53491 5.09773 6.71003 5.43093 6.02536 6.0996C5.34069 6.76827 4.99718 7.5684 4.99484 8.5C4.99251 9.4316 5.33601 10.2323 6.02536 10.9021C6.71471 11.5719 7.53959 11.9045 8.5 11.9C9.46041 11.8955 10.2859 11.5611 10.9764 10.9004Z"
                        />
                    </svg>
                </button>

                <img
                    v-if="owner.avatar?.profile_photo && !avatarFailed"
                    :src="owner.avatar.profile_photo"
                    :alt="ownerDisplayName"
                    class="relative z-10 size-[180px] rounded-full border-[8px] border-solid border-white object-cover lg:size-[190px] lg:border-[10px]"
                    @error="avatarFailed = true"
                />
                <div
                    v-else
                    class="relative z-10 grid size-[180px] place-items-center rounded-full border-[8px] border-solid border-white bg-brand-navy/80 font-brand-display text-[64px] font-black uppercase text-white lg:size-[190px] lg:border-[10px] lg:text-[80px]"
                    aria-hidden="true"
                >
                    {{ ownerDisplayName?.charAt(0) }}
                </div>

                <button
                    v-if="isOwner"
                    type="button"
                    class="grid size-10 place-items-center rounded-brand bg-black/25 text-white transition hover:bg-black/40 max-lg:mt-6 lg:hidden"
                    aria-label="Copy profile URL"
                    @click="copyProfileUrl"
                >
                    <svg
                        viewBox="0 0 16 18"
                        class="h-[18px] w-4"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            d="M13.5 0H5.4C4.41 0 3.6 0.81 3.6 1.8V12.6C3.6 13.59 4.41 14.4 5.4 14.4H13.5C14.49 14.4 15.3 13.59 15.3 12.6V1.8C15.3 0.81 14.49 0 13.5 0ZM13.5 12.6H5.4V1.8H13.5V12.6ZM0 11.7V9.9H1.8V11.7H0ZM0 6.75H1.8V8.55H0V6.75ZM6.3 16.2H8.1V18H6.3V16.2ZM0 14.85V13.05H1.8V14.85H0ZM1.8 18C0.81 18 0 17.19 0 16.2H1.8V18ZM4.95 18H3.15V16.2H4.95V18ZM9.45 18V16.2H11.25C11.25 17.19 10.44 18 9.45 18ZM1.8 3.6V5.4H0C0 4.41 0.81 3.6 1.8 3.6Z"
                        />
                    </svg>
                </button>
            </div>

            <!-- Name, badges, status bubble -->
            <div
                class="flex min-w-0 flex-1 flex-col items-center gap-3 max-lg:w-full lg:items-start"
            >
                <h1
                    class="font-brand-display text-[42px] font-black italic uppercase leading-[1.2] text-white max-lg:hidden"
                >
                    {{ heroTitle }}
                </h1>

                <!-- Mobile splits contributor icons left / member year right -->
                <div
                    class="flex w-full flex-wrap items-center gap-x-6 gap-y-2 font-brand text-[13px] font-semibold text-white max-lg:absolute max-lg:inset-x-6 max-lg:top-[196px] max-lg:w-auto max-lg:text-[#3c3938] lg:w-auto lg:justify-start"
                    :class="
                        owner.is_gift_hero
                            ? 'max-lg:justify-between'
                            : 'max-lg:justify-end'
                    "
                >
                    <div
                        v-if="owner.is_gift_hero"
                        class="flex items-center gap-x-6 gap-y-2"
                    >
                        <span class="inline-flex items-center gap-2">
                            <img
                                :src="
                                    asset(
                                        'images/profile/badge-top-contributor.png',
                                    )
                                "
                                alt=""
                                class="size-[22px]"
                            />
                            <span class="max-lg:hidden">Top Contributor</span>
                        </span>
                        <span class="inline-flex items-center gap-2">
                            <svg
                                viewBox="0 0 19 23"
                                class="h-[22px] w-[18px]"
                                fill="none"
                                aria-hidden="true"
                            >
                                <path
                                    d="M9.08823 0L0 4.01299V10.0325C0 15.6005 3.87765 20.8073 9.08823 22.0714C14.2988 20.8073 18.1765 15.6005 18.1765 10.0325V4.01299L9.08823 0Z"
                                    fill="url(#ghShieldHero)"
                                />
                                <path
                                    d="M13.9272 6.49583L10.2154 7.20229C10.0691 7.22851 9.96183 7.36463 9.96183 7.52409V10.6053C9.92691 10.6146 9.89128 10.624 9.8539 10.6322C9.63167 10.6825 9.36889 10.713 9.08811 10.713C8.83309 10.713 8.59252 10.6879 8.38406 10.6455C8.32409 10.6333 8.26766 10.6193 8.21369 10.6049V7.52445C8.21369 7.36499 8.10646 7.22887 7.96008 7.20265L4.24898 6.49619C4.06344 6.46279 3.89484 6.61615 3.89484 6.81799V14.5466C3.89484 14.7061 4.00207 14.8422 4.14845 14.8684L7.85955 15.5749C8.04508 15.6083 8.21369 15.4549 8.21369 15.2531V12.5508C8.4927 12.5961 8.78547 12.6194 9.08811 12.6198C9.3904 12.6198 9.68282 12.5957 9.96148 12.5508V15.2535C9.96148 15.4553 10.1304 15.6087 10.316 15.5753L14.0278 14.8688C14.1742 14.8426 14.2814 14.7065 14.2814 14.547V6.81835C14.2814 6.61651 14.1124 6.46315 13.9269 6.49655L13.9272 6.49583Z"
                                    fill="#fff"
                                />
                                <defs>
                                    <linearGradient
                                        id="ghShieldHero"
                                        x1="6.4916"
                                        y1="1.83929"
                                        x2="17.1247"
                                        y2="25.6074"
                                        gradientUnits="userSpaceOnUse"
                                    >
                                        <stop stop-color="#FFB545" />
                                        <stop offset="1" stop-color="#DB7712" />
                                    </linearGradient>
                                </defs>
                            </svg>
                            <span class="max-lg:hidden"
                                >Expert Contributor</span
                            >
                        </span>
                    </div>
                    <span
                        v-if="owner.member_since"
                        class="inline-flex items-center gap-2"
                    >
                        <svg
                            viewBox="0 0 18 21"
                            class="h-[21px] w-[18px]"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M16 2.025H15V1.0125C15 0.455625 14.55 0 14 0C13.45 0 13 0.455625 13 1.0125V2.025H5V1.0125C5 0.455625 4.55 0 4 0C3.45 0 3 0.455625 3 1.0125V2.025H2C1.46957 2.025 0.960859 2.23835 0.585786 2.61811C0.210714 2.99787 0 3.51294 0 4.05V18.225C0 19.3388 0.9 20.25 2 20.25H16C17.1 20.25 18 19.3388 18 18.225V4.05C18 2.93625 17.1 2.025 16 2.025ZM9 5.0625C10.66 5.0625 12 6.41925 12 8.1C12 9.78075 10.66 11.1375 9 11.1375C7.34 11.1375 6 9.78075 6 8.1C6 6.41925 7.34 5.0625 9 5.0625ZM15 17.2125H3V16.2C3 14.175 7 13.0613 9 13.0613C11 13.0613 15 14.175 15 16.2V17.2125Z"
                                fill="#FFB545"
                            />
                        </svg>
                        <span
                            ><span class="max-lg:hidden">Member since </span
                            >{{ owner.member_since }}</span
                        >
                    </span>
                </div>

                <!-- Status message bubble; straddles the hero edge on mobile -->
                <div
                    v-if="statusMessage || isOwner"
                    class="relative mt-1 max-w-full max-lg:z-20 max-lg:-mt-8 max-lg:w-full"
                >
                    <div
                        class="relative flex items-center gap-3 rounded-brand bg-white py-2.5 pl-4 pr-3 max-lg:rounded-[10px] max-lg:rounded-tl-none max-lg:border max-lg:border-solid max-lg:border-brand-tan max-lg:px-4 max-lg:py-3.5"
                    >
                        <!-- Speech tail (Figma "note" 224:633): 11x13.4,
                             centered on the card, ~1px overlap so the white
                             fill hides the border segment beneath it. -->
                        <svg
                            viewBox="0 0 24 20"
                            class="absolute -top-[19px] left-1/2 h-[20px] w-[24px] -translate-x-1/2 lg:hidden"
                            aria-hidden="true"
                        >
                            <path d="M0 20 L18 1 L18 20 Z" fill="#ffffff" />
                            <path
                                d="M0 20 L18 1 L18 20"
                                fill="none"
                                stroke="#cdc4c2"
                                stroke-width="1"
                            />
                        </svg>
                        <span
                            class="shrink-0 text-[30px] leading-none lg:text-[22px]"
                            aria-hidden="true"
                            >{{ welcomeEmoji }}</span
                        >
                        <p
                            class="min-w-0 flex-1 truncate font-brand text-[14px] font-semibold text-[#191e3e] max-lg:line-clamp-2 max-lg:whitespace-normal max-lg:leading-[1.25]"
                        >
                            {{ statusMessage || "Add a welcome message…" }}
                        </p>
                        <!-- Opens the same modalog as the gear (BH-40): the
                             message is edited in place instead of on the
                             account settings page. -->
                        <button
                            v-if="isOwner"
                            type="button"
                            class="shrink-0 text-[#565a77] transition hover:text-brand-navy max-lg:text-brand-navy"
                            aria-label="Edit welcome message"
                            @click="
                                openManageProfileModalog('short_description')
                            "
                        >
                            <svg
                                viewBox="0 0 10 10"
                                class="size-2.5 max-lg:size-[15px]"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    d="M6.58089 1.09767L7.35833 0.320225C7.45964 0.218715 7.57996 0.138181 7.71243 0.0832325C7.84489 0.0282839 7.98689 0 8.1303 0C8.27371 0 8.41571 0.0282839 8.54818 0.0832325C8.68064 0.138181 8.80097 0.218715 8.90227 0.320225L9.67972 1.09767C10.1068 1.52472 10.1068 2.21457 9.67972 2.64162L8.90227 3.41907L6.58089 1.09767ZM5.80345 1.87512L0 7.67861V10H2.32138L8.12483 4.19651L5.80345 1.87512Z"
                                />
                            </svg>
                        </button>
                    </div>
                    <div
                        class="absolute -left-1.5 bottom-1.5 size-3 rotate-45 bg-white max-lg:hidden"
                        aria-hidden="true"
                    ></div>
                </div>
            </div>

            <!-- Desktop owner actions -->
            <div
                v-if="isOwner"
                class="shrink-0 self-start max-lg:hidden lg:flex lg:flex-col lg:items-end lg:gap-4"
            >
                <button
                    type="button"
                    class="group flex items-center gap-3 font-brand text-[13px] font-bold text-white"
                    aria-label="Manage profile and theme"
                    @click="openManageProfileModalog"
                >
                    Manage Profile/Theme
                    <span
                        class="grid size-10 place-items-center rounded-brand bg-black/25 transition group-hover:bg-black/40"
                    >
                        <svg
                            viewBox="0 0 17 17"
                            class="size-[17px]"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                d="M6.74742 17L6.22165 14.45C5.87113 14.3083 5.54282 14.1599 5.2367 14.0046C4.93058 13.8493 4.61629 13.6578 4.29381 13.43L1.75258 14.195L0 11.305L2.01546 9.605C1.95704 9.23667 1.92783 8.86833 1.92783 8.5C1.92783 8.13167 1.95704 7.76333 2.01546 7.395L0 5.695L1.75258 2.805L4.29381 3.57C4.61512 3.34333 4.92942 3.15237 5.2367 2.9971C5.54399 2.84183 5.8723 2.6928 6.22165 2.55L6.74742 0H10.2526L10.7783 2.55C11.1289 2.69167 11.4578 2.8407 11.765 2.9971C12.0723 3.1535 12.386 3.34447 12.7062 3.57L15.2474 2.805L17 5.695L14.9845 7.395C15.043 7.76333 15.0722 8.13167 15.0722 8.5C15.0722 8.86833 15.043 9.23667 14.9845 9.605L17 11.305L15.2474 14.195L12.7062 13.43C12.3849 13.6567 12.0712 13.8482 11.765 14.0046C11.4589 14.161 11.13 14.3095 10.7783 14.45L10.2526 17H6.74742ZM10.9764 10.9004C11.6622 10.2363 12.0052 9.43613 12.0052 8.5C12.0052 7.56387 11.6622 6.76373 10.9764 6.0996C10.2905 5.43547 9.46509 5.10227 8.5 5.1C7.53491 5.09773 6.71003 5.43093 6.02536 6.0996C5.34069 6.76827 4.99718 7.5684 4.99484 8.5C4.99251 9.4316 5.33601 10.2323 6.02536 10.9021C6.71471 11.5719 7.53959 11.9045 8.5 11.9C9.46041 11.8955 10.2859 11.5611 10.9764 10.9004Z"
                            />
                        </svg>
                    </span>
                </button>
                <button
                    type="button"
                    class="group flex items-center gap-3 font-brand text-[13px] font-bold text-white"
                    aria-label="Copy profile URL"
                    @click="copyProfileUrl"
                >
                    {{ copied ? "Copied!" : "Copy Profile URL:" }}
                    <span
                        class="grid size-10 place-items-center rounded-brand bg-black/25 transition group-hover:bg-black/40"
                    >
                        <svg
                            viewBox="0 0 16 18"
                            class="h-[18px] w-4"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                d="M13.5 0H5.4C4.41 0 3.6 0.81 3.6 1.8V12.6C3.6 13.59 4.41 14.4 5.4 14.4H13.5C14.49 14.4 15.3 13.59 15.3 12.6V1.8C15.3 0.81 14.49 0 13.5 0ZM13.5 12.6H5.4V1.8H13.5V12.6ZM0 11.7V9.9H1.8V11.7H0ZM0 6.75H1.8V8.55H0V6.75ZM6.3 16.2H8.1V18H6.3V16.2ZM0 14.85V13.05H1.8V14.85H0ZM1.8 18C0.81 18 0 17.19 0 16.2H1.8V18ZM4.95 18H3.15V16.2H4.95V18ZM9.45 18V16.2H11.25C11.25 17.19 10.44 18 9.45 18ZM1.8 3.6V5.4H0C0 4.41 0.81 3.6 1.8 3.6Z"
                            />
                        </svg>
                    </span>
                </button>
            </div>

            <!-- Visitor: follow (or sign up to follow). Mobile renders the
                 follow CTA in the tabs' white zone instead (GH-1095, per
                 Figma 224-689), like the owner's Create List button. -->
            <div v-else class="shrink-0 max-lg:hidden lg:self-center">
                <!-- Primary CTA (BrandButton): navy that wipes red, or the
                     white "following" treatment; guests get the same button
                     pointed at sign-up. Mirrors FollowButton on list detail,
                     minus its tan ring: on the hero gradient the white fill
                     already reads as a button, and the ring showed as a pale
                     border during the navy wipe. -->
                <BrandButton
                    :href="authUser ? undefined : route('register')"
                    :variant="owner.following ? 'whiteOnRed' : 'navy'"
                    icon-rest-class="text-brand-red"
                    class="h-[52px] px-7 text-[17px]"
                    :loading="followBusy"
                    @click="authUser && toggleFollow()"
                >
                    <template #icon>
                        <!-- Even height keeps the glyph's centering offset
                             integral between the resting and hover-lift
                             layers (same fix as FollowButton). -->
                        <svg
                            viewBox="0 0 21 15"
                            class="h-4 w-[22px] shrink-0"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                d="M3.81818 10.3125L3.24545 9.84375C1.33636 8.0625 0 6.9375 0 5.53125C0 4.40625 0.954545 3.46875 2.1 3.46875C2.76818 3.46875 3.43636 3.75 3.81818 4.21875C4.2 3.75 4.86818 3.46875 5.53636 3.46875C6.68182 3.46875 7.63636 4.3125 7.63636 5.53125C7.63636 6.9375 6.3 8.0625 4.39091 9.84375L3.81818 10.3125ZM13.3636 0C12.351 0 11.3798 0.395088 10.6638 1.09835C9.94773 1.80161 9.54545 2.75544 9.54545 3.75C9.54545 4.74456 9.94773 5.69839 10.6638 6.40165C11.3798 7.10491 12.351 7.5 13.3636 7.5C14.3763 7.5 15.3474 7.10491 16.0635 6.40165C16.7795 5.69839 17.1818 4.74456 17.1818 3.75C17.1818 2.75544 16.7795 1.80161 16.0635 1.09835C15.3474 0.395088 14.3763 0 13.3636 0ZM13.3636 5.71875C12.832 5.71875 12.3221 5.51133 11.9462 5.14212C11.5703 4.7729 11.3591 4.27214 11.3591 3.75C11.3591 3.22785 11.5703 2.7271 11.9462 2.35788C12.3221 1.98867 12.832 1.78125 13.3636 1.78125C13.6269 1.78125 13.8875 1.83217 14.1307 1.93111C14.3739 2.03005 14.5949 2.17507 14.7811 2.35788C14.9672 2.5407 15.1149 2.75773 15.2156 2.99659C15.3163 3.23545 15.3682 3.49146 15.3682 3.75C15.3682 4.00854 15.3163 4.26455 15.2156 4.50341C15.1149 4.74227 14.9672 4.9593 14.7811 5.14212C14.5949 5.32493 14.3739 5.46995 14.1307 5.56889C13.8875 5.66783 13.6269 5.71875 13.3636 5.71875ZM13.3636 8.4375C10.815 8.4375 5.72727 9.68437 5.72727 12.1875V15H21V12.1875C21 9.68437 15.9123 8.4375 13.3636 8.4375ZM19.1864 13.2188H7.54091V12.1875C7.54091 11.5875 10.5 10.2188 13.3636 10.2188C16.1986 10.2188 19.1864 11.5875 19.1864 12.1875V13.2188Z"
                            />
                        </svg>
                    </template>
                    {{ followLabel }}
                </BrandButton>
            </div>
        </div>
    </section>
</template>
